import { useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { createCategory, updateCategory, deleteCategory, fetchBlogCategories } from '../store/blogSlice';
import type { Category } from '../types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

interface CategoryManagerProps {
  blogId: number;
  categories: Category[];
  isAdmin: boolean;
  selectedCategoryId: number | null;
  onSelectCategory: (id: number | null) => void;
}

export const CategoryManager = ({
  blogId,
  categories,
  isAdmin,
  selectedCategoryId,
  onSelectCategory,
}: CategoryManagerProps) => {
  const dispatch = useAppDispatch();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const handleCreateCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Vui lòng nhập tên danh mục');
      return;
    }

    try {
      await dispatch(
        createCategory({
          blog_id: blogId,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        })
      ).unwrap();

      toast.success('Tạo danh mục thành công');
      setShowCreateDialog(false);
      setFormData({ name: '', description: '' });
    } catch (error: any) {
      toast.error(error?.message || 'Không thể tạo danh mục');
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory || !formData.name.trim()) {
      toast.error('Vui lòng nhập tên danh mục');
      return;
    }

    try {
      await dispatch(
        updateCategory({
          id: editingCategory.id,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
          },
        })
      ).unwrap();

      toast.success('Cập nhật danh mục thành công');
      setShowEditDialog(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
    } catch (error: any) {
      toast.error(error?.message || 'Không thể cập nhật danh mục');
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;

    try {
      await dispatch(deleteCategory(deletingCategory.id)).unwrap();
      toast.success('Xóa danh mục thành công');
      setShowDeleteDialog(false);
      setDeletingCategory(null);
      if (selectedCategoryId === deletingCategory.id) {
        onSelectCategory(null);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Không thể xóa danh mục');
    }
  };

  const openEditDialog = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (category: Category) => {
    setDeletingCategory(category);
    setShowDeleteDialog(true);
  };

  const handleMoveUp = async (category: Category, index: number) => {
    if (index === 0) return; // Already at top

    const prevCategory = categories[index - 1];
    const tempOrder = category.sort_order;

    try {
      // Swap sort_order
      await dispatch(
        updateCategory({
          id: category.id,
          data: { sort_order: prevCategory.sort_order },
        })
      ).unwrap();

      await dispatch(
        updateCategory({
          id: prevCategory.id,
          data: { sort_order: tempOrder },
        })
      ).unwrap();

      // Refresh categories to get updated order from server
      await dispatch(fetchBlogCategories({ blogId })).unwrap();

      toast.success('Đã di chuyển danh mục lên');
    } catch (error: any) {
      toast.error(error?.message || 'Không thể di chuyển danh mục');
    }
  };

  const handleMoveDown = async (category: Category, index: number) => {
    if (index === categories.length - 1) return; // Already at bottom

    const nextCategory = categories[index + 1];
    const tempOrder = category.sort_order;

    try {
      // Swap sort_order
      await dispatch(
        updateCategory({
          id: category.id,
          data: { sort_order: nextCategory.sort_order },
        })
      ).unwrap();

      await dispatch(
        updateCategory({
          id: nextCategory.id,
          data: { sort_order: tempOrder },
        })
      ).unwrap();

      // Refresh categories to get updated order from server
      await dispatch(fetchBlogCategories({ blogId })).unwrap();

      toast.success('Đã di chuyển danh mục xuống');
    } catch (error: any) {
      toast.error(error?.message || 'Không thể di chuyển danh mục');
    }
  };

  return (
    <div>
      {/* Categories List - Minimal clean design */}
      <div className="space-y-1">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className={`group transition-colors ${
              selectedCategoryId === category.id
                ? 'bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="w-full px-6 py-2.5 flex items-center justify-between gap-2">
              <button
                className="flex-1 text-left"
                onClick={() => onSelectCategory(category.id)}
              >
                <span className={`text-sm font-medium ${
                  selectedCategoryId === category.id
                    ? 'text-blue-700'
                    : 'text-gray-700'
                }`}>
                  {category.name}
                </span>
              </button>
              {isAdmin && (
                <div className="flex gap-1">
                  {/* Up/Down buttons */}
                  <button
                    className={`p-1.5 hover:bg-gray-200 rounded transition-colors ${
                      index === 0 ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveUp(category, index);
                    }}
                    disabled={index === 0}
                    aria-label="Di chuyển lên"
                    title="Di chuyển lên"
                  >
                    <ChevronUp className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    className={`p-1.5 hover:bg-gray-200 rounded transition-colors ${
                      index === categories.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveDown(category, index);
                    }}
                    disabled={index === categories.length - 1}
                    aria-label="Di chuyển xuống"
                    title="Di chuyển xuống"
                  >
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(category);
                    }}
                    aria-label="Sửa danh mục"
                    title="Sửa danh mục"
                  >
                    <Edit className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    className="p-1.5 hover:bg-red-100 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog(category);
                    }}
                    aria-label="Xóa danh mục"
                    title="Xóa danh mục"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state and Add button */}
      {categories.length === 0 && (
        <p className="text-sm text-gray-500 px-6 py-8 text-center">
          Chưa có danh mục
        </p>
      )}

      {isAdmin && (
        <button
          onClick={() => setShowCreateDialog(true)}
          className="w-full px-6 py-3 flex items-center gap-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors mt-2"
        >
          <Plus className="h-4 w-4" />
          Tạo danh mục mới
        </button>
      )}

      {/* Create Category Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tạo Danh Mục Mới</DialogTitle>
            <DialogDescription>
              Thêm danh mục mới để tổ chức bài viết
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tên danh mục</label>
              <Input
                placeholder="Ví dụ: Công nghệ"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Mô tả (tùy chọn)</label>
              <Textarea
                placeholder="Mô tả ngắn về danh mục..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Hủy
            </Button>
            <Button onClick={handleCreateCategory}>Tạo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Chỉnh Sửa Danh Mục</DialogTitle>
            <DialogDescription>
              Cập nhật thông tin danh mục
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tên danh mục</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Mô tả</label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Hủy
            </Button>
            <Button onClick={handleEditCategory}>Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa danh mục?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa danh mục "{deletingCategory?.name}"? Các bài
              viết trong danh mục này sẽ không bị xóa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory}>Xóa</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
